import { ref } from 'vue';
import type { FieldSchema } from '../types';

interface PasswordPolicyContext {
  email?: string;
  shelter_name?: string;
  first_name?: string;
  last_name?: string;
}

type PasswordStrength = 'empty' | 'weak' | 'medium' | 'strong';

// NOTE :: This must be Dropbox's ORIGINAL `zxcvbn` package (not @zxcvbn-ts), because the backend
// uses bjeavons/zxcvbn-php which is a port of that original algorithm + dictionaries. The two produce
// identical scores; @zxcvbn-ts is a rewrite that scores differently and caused client/server drift.
type ZxcvbnFn = (password: string, userInputs?: string[]) => { score: number };

// NOTE :: Minimum length is the only structural rule. We intentionally do NOT require uppercase,
// lowercase, digits, or special characters, and spaces are allowed — length + zxcvbn (below) is a
// stronger, less frustrating policy than character-class complexity. Mirrors the backend.
const MIN_PASSWORD_LENGTH = 12;

// NOTE :: zxcvbn score is 0 (weakest) .. 4 (strongest). 3 = "safely unguessable"; reject below this.
// Mirrors the backend PasswordPolicyService so the meter never disagrees with server validation.
const MIN_ZXCVBN_SCORE = 3;

// zxcvbn bundles sizable frequency dictionaries, so it is lazy-loaded on first use to keep it out of
// the main bundle. `zxcvbnReady` is reactive so the strength meter re-computes once it loads.
const zxcvbnReady = ref(false);
let zxcvbnFn: ZxcvbnFn | null = null;
let zxcvbnLoadPromise: Promise<void> | null = null;

function ensureZxcvbnLoaded(): Promise<void> {
  if (zxcvbnLoadPromise) return zxcvbnLoadPromise;

  zxcvbnLoadPromise = import('zxcvbn')
    .then((mod) => {
      zxcvbnFn = ((mod as unknown as { default?: ZxcvbnFn }).default ?? mod) as ZxcvbnFn;
      zxcvbnReady.value = true;
    })
    .catch(() => {
      // NOTE :: If the estimator fails to load we fall back to complexity-only on the client; the
      // backend still enforces the full zxcvbn policy, so the gate is never weakened.
      zxcvbnLoadPromise = null;
    });

  return zxcvbnLoadPromise;
}

/**
 * Returns the zxcvbn score (0..4) for the password, or null when the estimator has not loaded yet.
 * Reads `zxcvbnReady` so reactive callers (the strength meter) recompute once it becomes available.
 */
function getZxcvbnScore(password: string, context: PasswordPolicyContext): number | null {
  if (!zxcvbnReady.value || !zxcvbnFn) {
    void ensureZxcvbnLoaded();
    return null;
  }

  return zxcvbnFn(password, buildContextTerms(context)).score;
}

// Domain/common banned terms — rejected as a substring anywhere in the password. Active validation,
// running alongside zxcvbn: zxcvbn scores guessability; these catch shelter/app-specific words it won't.
const STATIC_BANNED_TERMS = [
  'password',
  '123456',
  '12345678',
  '123456789',
  'qwerty',
  'animalsfirst',
  'shelter',
  'volunteer',
  'animal',
  'letmein',
  'welcome',
  'admin',
  'login',
  'passw0rd',
  'iloveyou',
  'monkey',
  'dragon',
  'master',
  'sunshine',
  'princess',
  'football',
  'shadow',
  'abc123',
  'baseball',
  'trustno1',

  // Shelter / rescue / adoption / foster domain + brand and leetspeak evasions (substring,
  // case-insensitive). Includes the short animal roots (dog / cat / pet / paw) by product decision —
  // as substrings they can flag innocent passwords (e.g. "cat" in "communicate"); accepted tradeoff.
  'foster',
  'rescue',
  'rehome',
  'adopt',
  'kennel',
  'humane',
  'spca',
  'aspca',
  'wildlife',
  'veterinary',
  'veterinarian',
  'puppy',
  'puppies',
  'kitten',
  'kitty',
  // 'dog',
  // 'cat',
  // 'pet',
  // 'paw',
  'critter',
  'furbaby',
  'sh3lt3r',
  '4n1m4l',
  'v0lunt33r',
  '@nim@l',
];

const PASSWORD_POLICY_ERROR_KEYS = {
  LENGTH: 'components.formFields.password.errors.length',
  BANNED_WORD: 'components.formFields.password.errors.bannedWord',
  CONTEXT: 'components.formFields.password.errors.context',
  COMMON: 'components.formFields.password.errors.common',
} as const;

interface PasswordRequirement {
  key: string;
  met: boolean;
}

// Per-requirement predicates, keyed by the last segment of the requirement i18n key
// (e.g. "...requirements.rules.lengthOf12" -> "lengthOf12"). Used to render the live checklist.
const PASSWORD_RULE_CHECKS: Record<string, (password: string) => boolean> = {
  lengthOf12: (password) => password.length >= MIN_PASSWORD_LENGTH,
};

/**
 * Maps each configured requirement i18n key to whether the password currently satisfies it, so the
 * password field can render a live red ✗ / green ✓ checklist. Unknown keys default to unmet.
 */
function getPasswordRequirements(password: string, rules: string[] = []): PasswordRequirement[] {
  return rules.map((key) => {
    const id = key.split('.').pop() ?? key;
    const check = PASSWORD_RULE_CHECKS[id];

    return { key, met: !!password && !!check && check(password) };
  });
}

function buildContextTerms(context: PasswordPolicyContext): string[] {
  const terms: string[] = [];
  const email = (context.email ?? '').trim().toLowerCase();

  if (email) {
    terms.push(email);

    const localPart = email.split('@')[0] ?? '';
    if (localPart.length >= 3) terms.push(localPart);
  }

  (['shelter_name', 'first_name', 'last_name'] as const).forEach((key) => {
    const value = (context[key] ?? '').trim().toLowerCase();

    if (value.length >= 3) terms.push(value);
  });

  return [...new Set(terms)];
}

/**
 * Returns the blocklist error key (banned word or personal-info substring) for the password, or null
 * when it clears both. Mirrors the backend's STATIC_BANNED_TERMS + buildContextTerms substring checks.
 * The backend's 10,000 common-password file is intentionally NOT mirrored here (it stays server-side);
 * zxcvbn covers the bulk of those client-side and the server remains authoritative.
 */
function getBlocklistError(password: string, context: PasswordPolicyContext = {}): string | null {
  const passwordLower = password.toLowerCase();

  if (STATIC_BANNED_TERMS.some((term) => passwordLower.includes(term.toLowerCase()))) {
    return PASSWORD_POLICY_ERROR_KEYS.BANNED_WORD;
  }

  // Personal-info match is case-insensitive AND space-insensitive, so e.g. shelter "Happy Paws" is
  // caught in both "happy paws..." and "happypaws...".
  const passwordCompact = passwordLower.replace(/\s+/g, '');

  const containsContextTerm = buildContextTerms(context).some((term) => {
    if (term === '') return false;
    if (passwordLower.includes(term)) return true;

    const termCompact = term.replace(/\s+/g, '');
    return termCompact !== '' && passwordCompact.includes(termCompact);
  });

  if (containsContextTerm) return PASSWORD_POLICY_ERROR_KEYS.CONTEXT;

  return null;
}

/**
 * Active password policy, mirroring the backend in the same order: minimum length (12, spaces
 * allowed) -> zxcvbn (score >= 3) -> banned-word substrings -> personal-info substrings. Each layer
 * is only evaluated once the prior ones pass, so the user sees one actionable message at a time.
 * Until zxcvbn finishes loading the client falls back to the non-zxcvbn layers (the backend still
 * enforces the full policy, so this never weakens the gate).
 */
function getPasswordValidationErrors(password: string, context: PasswordPolicyContext = {}): string[] {
  if (!password) return [];

  const errors: string[] = [];

  if (password.length < MIN_PASSWORD_LENGTH) errors.push(PASSWORD_POLICY_ERROR_KEYS.LENGTH);

  if (errors.length === 0) {
    const score = getZxcvbnScore(password, context);
    if (score !== null && score < MIN_ZXCVBN_SCORE) errors.push(PASSWORD_POLICY_ERROR_KEYS.COMMON);
  }

  if (errors.length === 0) {
    const blocklistError = getBlocklistError(password, context);
    if (blocklistError) errors.push(blocklistError);
  }

  return errors;
}

/**
 * Strength derived from the SAME policy the backend enforces, so the meter never disagrees with
 * validation (PrimeVue's built-in meter is regex-only and would mark e.g. "Sunflower2024!" as strong
 * even though zxcvbn rates it as guessable).
 *
 * - weak   : shorter than the minimum length requirement.
 * - medium : meets the length requirement but zxcvbn score < 3, contains a banned word / personal
 *            info (backend STILL rejects it), or the estimator is still loading.
 * - strong : meets the length requirement, zxcvbn score >= 3, and clears the blocklist -> backend accepts it.
 */
function getPasswordStrength(password: string, context: PasswordPolicyContext = {}): PasswordStrength {
  if (!password) return 'empty';

  if (password.length < MIN_PASSWORD_LENGTH) return 'weak';

  // A banned-word / personal-info hit is a hard server-side rejection; never show it as strong.
  if (getBlocklistError(password, context)) return 'medium';

  const score = getZxcvbnScore(password, context);
  if (score === null) return 'medium'; // estimator still loading; stay conservative until ready

  return score >= MIN_ZXCVBN_SCORE ? 'strong' : 'medium';
}

function applyPasswordPolicyContext(schema: FieldSchema[], context: PasswordPolicyContext) {
  const passwordField = schema.find((field) => field.name === 'password');

  if (!passwordField) return;

  passwordField.settings.passwordPolicy = true;
  passwordField.settings.passwordContext = context;
}

function buildPasswordPolicyContextFromSchema(schema: FieldSchema[]): PasswordPolicyContext {
  const getValue = (name: string) => {
    const field = schema.find((item) => item.name === name);

    return field?.model ? String(field.model) : '';
  };

  return {
    email: getValue('email'),
    shelter_name: getValue('shelter'),
    first_name: getValue('first_name'),
    last_name: getValue('last_name'),
  };
}

// Begin loading the estimator as soon as a screen pulls in this helper, so it is almost always
// ready by the time the user finishes typing their password.
void ensureZxcvbnLoaded();

export {
  PASSWORD_POLICY_ERROR_KEYS,
  applyPasswordPolicyContext,
  buildPasswordPolicyContextFromSchema,
  ensureZxcvbnLoaded,
  getPasswordRequirements,
  getPasswordStrength,
  getPasswordValidationErrors,
};

export type { PasswordPolicyContext, PasswordRequirement, PasswordStrength };
